import { dom, Checkbox, getThemeManager, log } from '@omegagrid/core';
import { Code } from '@omegagrid/code';
import container, {
	Router, ServiceErrorEvent, AppHost, Container,
	AppInfo, Application, GlobalOptions, Auth,
	CoreApplication, ServiceCollectionEvent, AccountingProvider
} from '@reporting/rp-app-host';
import { Omegagrid } from '@reporting/rp-app-host/src/omegagrid';
import { ReportingAuthClient } from '@reporting/rp-auth-client-v2';
import { Accounting } from '@reporting/rp-types/core';
import { AccountingApplication } from '@reporting/rp-mf-accounting';
import { AnalyticsApplication } from '@reporting/rp-mf-analytics';
import { DeploymentApplication } from '@reporting/rp-mf-deployment';
import { DefinitionApplication } from '@reporting/rp-mf-definition';
import { ETLApplication } from '@reporting/rp-mf-etl';
import { IOApplication } from '@reporting/rp-mf-io';
import { ExtensionApplication, ExtensionSandboxHost } from '@reporting/rp-mf-ext';
import { UserApplication } from '@reporting/rp-mf-users';
import { UserProvider } from '@reporting/rp-app-host';
import { HelpdeskApplication } from '@reporting/rp-mf-helpdesk';
import { MdmApplication } from '@reporting/rp-mf-mdm';
import { DWHApplication } from '@reporting/rp-mf-dwh';
import { WikiApplication } from '@reporting/rp-mf-wiki';
import { AdminApplication } from '@reporting/rp-mf-admin';
import { AIApplication } from '@reporting/rp-mf-ai';
import { SuperApplication } from '@reporting/rp-mf-super';
import { JobsApplication } from '@reporting/rp-mf-jobs';
import { AppApplication } from '@reporting/rp-mf-apps';
import { debounce } from 'ts-debounce';
import { formatTitle } from './utils';
import constants from './constants';

export * from '@reporting/rp-mf-jobs';

Checkbox.defaultIconSet = 'fas';
Checkbox.defaultAlternateIconSet = 'fal';
Code.defaultZIndex = 450;
ExtensionSandboxHost.defaultZIndex = 450;

log.registerLogger('rp', '⬢reporting');
log.registerLogger('rpex', '⬢reporting-extension');

declare global {
	interface Window {
		Omegagrid: typeof Omegagrid;
		rp: Container
	}
}

const GLOBAL_OPTIONS: GlobalOptions = {
	defaultServiceOptions: {
		baseUrl: constants.GATEWAY_URL,
		throwExceptions: true,
		showAlerts: true,
		// xdebugSession: 'CODE'
	},
	loginUrl: constants.CLIENT_AUTH_URL,
	gatewayUrl: constants.GATEWAY_URL,
	clientUrl: constants.CLIENT_URL,
	clientAuthUrl: constants.CLIENT_AUTH_URL,
	staticUrl: constants.STATIC_URL,
	appHash: constants.PACKAGE_VERSION_HASH
};

const appFactory = new Map<string, (appInfo: AppInfo) => Application>([
	['core', (appInfo) => new CoreApplication(appInfo)],
	['analytics', (appInfo) => new AnalyticsApplication(appInfo)],
	['etl', (appInfo) => new ETLApplication(appInfo)],
	['ai', (appInfo) => new AIApplication(appInfo)],
	['applications', (appInfo) => new AppApplication(appInfo)],
	['io', (appInfo) => new IOApplication(appInfo)],
	['extensions', (appInfo) => new ExtensionApplication(appInfo)],
	['mdm', (appInfo) => new MdmApplication(appInfo)],
	['dwh', (appInfo) => new DWHApplication(appInfo)],
	['deployment', (appInfo) => new DeploymentApplication(appInfo)],
	['definition', (appInfo) => new DefinitionApplication(appInfo)],
	['jobs', (appInfo) => new JobsApplication(appInfo)],
	['admin', (appInfo) => new AdminApplication(appInfo)],
	['super', (appInfo) => new SuperApplication(appInfo)],
	['wiki', (appInfo) => new WikiApplication(appInfo)],
	['helpdesk', (appInfo) => new HelpdeskApplication(appInfo)],
	['users', (appInfo) => new UserApplication(appInfo)],
	['accounting', (appInfo) => new AccountingApplication(appInfo)],
]);

const anonymousApps = ['wiki'];

let refreshingToken = false;
let refreshTokenResolveQueue: ((value: boolean) => void)[] = [];

const tryRefreshToken = async (authClient: ReportingAuthClient) =>  new Promise<boolean>((resolve) => {
	if (refreshingToken) {
		refreshTokenResolveQueue.push(resolve);
		return;
	} else {
		refreshingToken = true;
		refreshTokenResolveQueue = [resolve];
	}

	authClient.refreshToken().then(() => {
		refreshTokenResolveQueue.forEach(r => r(true));
		refreshTokenResolveQueue = [];
		refreshingToken = false;
	}).catch(() => {
		refreshTokenResolveQueue.forEach(r => r(false));
		refreshTokenResolveQueue = [];
		refreshingToken = false;
	});
});

const onServiceError = (e: ServiceErrorEvent, authClient: ReportingAuthClient, showAlerts: boolean = false) => {
	if (authClient && e.error.status == 401) {
		if (e.error.error == 'invalid_token') {
			authClient.redirectToLogin();
		} else if (e.error.error == 'expired_token') {
			tryRefreshToken(authClient).then(refreshed => {
				if (!refreshed) authClient.redirectToLogin();
			});
		}
	} else if (!container) {
		handleInitError('initialization error');
	} else if (showAlerts) {
		container.host?.bucket?.showAlert({
			title: 'Service error',
			content: `${e.error.status}: ${e.error.message || e.error.error || 'unknown'}`,
			type: 'danger',
		});
	}
}

const createApplications = (container: Container, apps: AppInfo[]) => {
	container.applications.clear();
	// support apps: never returned by /session and never in the side menu (no sideMenu flag),
	// they exist only so their components can be resolved from any module
	container.applications.register(appFactory.get('core')({name: 'core', text: 'Core'}));
	container.applications.register(appFactory.get('users')({name: 'users', text: 'Users'}));
	container.applications.register(appFactory.get('accounting')({name: 'accounting', text: 'Accounting'}));

	apps.forEach(appInfo => {
		if (!appFactory.has(appInfo.name)) return;
		container.applications.register(appFactory.get(appInfo.name)(appInfo));
	});
}

type UserSession = {
	apps: AppInfo[];
	settings: Record<string, any>;
	rights: string[];
	accounting: Accounting | null;
	group_admin: boolean;
};

// Single bootstrap call aggregating apps, settings, rights and the current accounting
const loadSession = async (container: Container) => {
	const session = await container.ms('users').get<UserSession>('session');
	container.userProvider.setRights(session.rights);
	container.userProvider.setGroupAdmin(!!session.group_admin);
	if (session.accounting) container.accountingProvider.accounting = session.accounting;
	createApplications(container, session.apps);
	await container.settings.init(session.settings);
}

const createAppHost = async (container: Container) => {
	const host = dom.createElement<AppHost>('rp-app-host');
	container.host = host;
	host.container = container;

	const accounting = container.accountingProvider.accounting;

	if (container.userProvider.isGuest()) {
		const app = container.applications.get(container.router.app);
		let title = 'Reporting.bi' + (app ? ` - ${app.info.description || app.info.name}` : '');
		window.document.title = title;
		host.title = title;
	} else {
		window.document.title = formatTitle(container);
		host.title = `#${accounting.accounting} ${accounting.company}`;
    }

	const router = container.router;
	host.updateComplete.then(async () => {
		if (router.hostState) host.setState(router.hostState)
		// TOOD: it should wait for the initialization of all components
		host.addEventListener('state', debounce(() => router.hostState = host.getState(), 1000));
	});

	return host;
}

const createExtensionHost = async (container: Container) => dom.createElement<ExtensionSandboxHost>('rp-extensionhost-sandbox', {
	container: container
});

const createHost = async (container: Container, parentElement: HTMLElement) => {
	const host = container.router.app == 'extensions' ? await createExtensionHost(container) : await createAppHost(container);
	initHomeButton(container);
	parentElement.innerHTML = '';
	parentElement.appendChild(host);
	return host;
}

const createAuthClient = () => new ReportingAuthClient({
	serviceUrl: `${constants.GATEWAY_URL}/auth`,
	loginUrl: constants.CLIENT_AUTH_URL,
});

const createContainer = () => {
	window.rp = container;
	container.options = GLOBAL_OPTIONS;
	container.meta = {package: {name: constants.PACKAGE_NAME, version: constants.PACKAGE_VERSION}};

	container.services.addEventListener('register', (e: ServiceCollectionEvent) => {
		e.service.addEventListener('error', (err: ServiceErrorEvent) => onServiceError(err, container.auth.client, err.showAlerts ?? e.service.options.showAlerts));
		e.service.onBeforeRequest = async (m, r) => {
			if (container.auth.client.expiresIn == null || container.auth.client.expiresIn > constants.ACCESS_TOKEN_REFRESH_THRESHOLD) return;
			if (!await tryRefreshToken(container.auth.client)) container.auth.client.redirectToLogin();
		};
	});

	const userService = container.services.register('users');
	const accountingService = container.services.register('accounting');
	const authClient = createAuthClient();
	container.auth = new Auth(authClient);
	container.accountingProvider = new AccountingProvider(accountingService);
	container.userProvider = new UserProvider(userService);

	container.services.register('jobs');
	container.services.register('etl');
	container.services.register('export'); //, {baseUrl: constants.EXPORT_URL, urlResolver: (path: string) => constants.EXPORT_URL + '/' + path});

	return container;
}

/**
 * Tells the server the user entered a company. The access log row feeds the recent companies of the
 * company switcher and becomes the user's LAST_ACCOUNTING, where the next login without a company in
 * the URL starts. An admin looking around as someone else leaves no trace in that user's history.
 */
const recordAccountingSwitch = (container: Container, previous: number | null, current: number) => {
	if (container.userProvider.impersonator) return;
	container.auth.client.log('switch_accounting', { result: `${previous ?? '-'} => ${current}` }, current)
		.then(() => { if (container.userProvider.user) container.userProvider.user.last_accounting = current; })
		.catch(() => {});
}

/**
 * Records the company the page opened in (the URL, or LAST_ACCOUNTING when the URL names none) as a
 * switch when it is not the one the user left last time (a new tab, a link, a bookmark). In-app
 * switches are recorded by the router; this runs once per page load.
 */
const syncAccounting = (container: Container) => {
	const a = container.accountingProvider.a;
	const user = container.userProvider.user;
	if (a == null || container.userProvider.isGuest() || !user || user.last_accounting === a) return;
	recordAccountingSwitch(container, user.last_accounting ?? null, a);
}

const createRouter = async (container: Container) => {
	container.router = new Router(location.href, container.accountingProvider, {
		baseUrl: constants.CLIENT_URL,
		apps: [...appFactory.keys()],
		anonymousApps: anonymousApps,
	});

	if (container.auth.client?.options?.accounting) {
		container.auth.client.options.accounting = container.accountingProvider.a;
	}

	container.router.addEventListener('route', async () => {
		container.applications.clear();
		container.create();
		if (container.auth.client?.options?.accounting) {
			const previous = container.auth.client.options.accounting;
			const current = container.accountingProvider.a;
			container.auth.client.options.accounting = current;
			if (previous && current && previous !== current) recordAccountingSwitch(container, previous, current);
		}
	});
}

const initGlobals = () => {
	window.Omegagrid = Omegagrid;
	Omegagrid.core.AMD.require = window.require;
	Omegagrid.code.loader.init(`${GLOBAL_OPTIONS.staticUrl}/libs/monaco-editor/min/vs`);
	Omegagrid.markdown.loader.init('https://cdn.jsdelivr.net/npm/@omegagrid/milkdown-kit@latest/dist/milkdown-kit.js');
	Omegagrid.plugins.xlsx.loader.init('https://report.reporting.cz/public/lib/sheetjs/dist');
	Omegagrid.plugins.charts.loader.init([
		'https://cdn.jsdelivr.net/npm/highcharts@13.0.0/highcharts.min.js',
		'https://cdn.jsdelivr.net/npm/highcharts@13.0.0/highcharts-more.min.js',
		'https://cdn.jsdelivr.net/npm/highcharts@13.0.0/modules/accessibility.min.js',
	]);
}

const handleInitError = (error: string) => {
	console.error(error);
	document.body.innerHTML = `
		<div style="${[
			'width: 100%',
			'height: 100%',
			'display: flex',
			'flex-direction: column',
			'justify-content: center',
			'align-items: center',
			'background-color: ' + (getThemeManager().activeTheme?.type == 'dark' ? '#111' : '#fff'),
		].join(';')}">
			<img src="${GLOBAL_OPTIONS.staticUrl}/assets/logo-dead.svg" style="width: 200px; height: 200px">
			<div style="color: red; font-size: 2em">${error}</div>
		</div>
	`;
}

const initLocale = async (container: Container) => {
	if (container.userProvider?.user?.lang) {
		await container.localization.setLocale(container.userProvider.user.lang);
	} else {
		await container.localization.setDefaultLocale();
	}
}

/**
 * Serves the page to a guest (no session) on an address a guest may open: the router then writes the
 * address without company and state and the host shows the one component (Router.anonymousAccess).
 */
const continueAsGuest = (container: Container) => {
	container.router.guest = true;
	container.router.accounting = -1;
}

const initUser = async (container: Container) => {
	const authClient = container.auth.client;
	let tokenInfo = await authClient.getTokenInfo();

	if (!tokenInfo?.valid) {
		const refreshed = await tryRefreshToken(authClient);
		tokenInfo = refreshed ? await authClient.getTokenInfo() : null;
		if (!tokenInfo?.valid) {
			if (container.router.anonymousApp) {
				continueAsGuest(container);
				return;
			} else {
				return authClient.redirectToLogin();
			}
		}
	}

	try {
		container.userProvider.impersonator = tokenInfo.claims?.impersonator as string;
		await container.userProvider.loadUser();
		if (!container.accountingProvider.a && container.userProvider.user.last_accounting) {
			container.router.accounting = container.userProvider.user.last_accounting
		}
	} catch (error) {
		if (container.router.anonymousApp) {
			continueAsGuest(container);
			return;
		} else {
			return authClient.redirectToLogin();
		}
	}
}

const openDefault = async (container: Container) => {
	const router = container.router;
	if (router?.app) {
		container.host.openSidebarComponent(router.app);
		container.applications.get(router.app)?.openPath(router.path.join('/'));
		return;
	}
	
	if (router.afterInit) {
		container.host.openHome();
	} else if (container.settings.get('host.home.auto_open')) {
		// automatically open homepage if auto_open is true and no tabs are open
		let hasTabs = false;
		container.host.bucket.editorContainer.model.walk(() => {
			hasTabs = true;
			return false;
		});
		if (!hasTabs) container.host.openHome();
	}
}

const initHomeButton = (container: Container) => {
	const homeContextId = container.settings.get('host.home.id_context');
	if (homeContextId && container.applications.items.has('analytics')) {
		container.host.homeComponentId = `analytics-homeReportContainer-${homeContextId}`;
		return;
	}

	// TODO: deprecated: later will be removed
	const app = [...container.applications.items.values()].find(app => app.info.homepage);
	if (app) {
		container.host.homeComponentId = `${app.name}-${app.info.homepage}`;
		return;
	}
}


const init = async () => {
	initGlobals();

	const container = createContainer();

	container.create = async () => {
		await container.init();
		await createRouter(container);
		await initUser(container);
		await Promise.all([
			initLocale(container),
			loadSession(container),
		]);

		container.theme.init();
		await createHost(container, document.body);
	};

	await container.create();
	syncAccounting(container);

	// TODO: bind this to some initialization event
	setTimeout(() => openDefault(container), 500);
}

document.addEventListener("DOMContentLoaded", () => {
	try {
		init();
	} catch (error) {
		handleInitError('initialization error');
	}
});
